import React, { ReactElement, FunctionComponent, memo, useMemo } from 'react'
import FloatingText from '@sport1/react-elements/FloatingText'
import Grid from '@sport1/react-elements/Grid'
import Touchable from '@sport1/react-elements/Touchable'
import { TestID } from '../utils/PropTypesDefinitions'
import BasicText from '../Theme/Fonts/BasicText'
import LinkProps from '../Link/LinkProps'
import AuthorImage from './AuthorImage'

export type Author = {
    /** Author's name */
    name: string
    /** Author's image url */
    imageUrl: string
    /** info about imageUrl for seo, optional field */
    alt?: string
    /** callback function for creating the url so user can click on author name
     *
     * @TJS-type function
     */
    createHref?: (index: number) => string
    /** callback function when the user presses
     *
     * @TJS-type function
     */
    onPress?: (index: number) => void
}

export type AuthorAreaProps = {
    /** prefixString for label */
    prefixString?: string
    /** array of authors */
    authors?: Author[]
    /** text under the authors about the publishing */
    published: string
    /** date time used for <time> */
    dateTime?: string
} & LinkProps &
    TestID

const MOBILE_IMAGE_SIZE = 36
const TABLET_IMAGE_SIZE = 40
const DESKTOP_IMAGE_SIZE = 44
export const HALF_MOBILE_IMAGE_SIZE = MOBILE_IMAGE_SIZE / 2

const IMAGES_SIZES = [MOBILE_IMAGE_SIZE, TABLET_IMAGE_SIZE, DESKTOP_IMAGE_SIZE]

const COMPONENT_HEIGHTS = [MOBILE_IMAGE_SIZE + 8, TABLET_IMAGE_SIZE + 8, DESKTOP_IMAGE_SIZE + 8]

type AuthorTextStyle = {
    fontSize?: string | string[]
    lineHeight?: string | string[]
    color?: string
    textDecorationLine?: 'underline' | 'none'
}

type AuthorTextProps = {
    content: string
    textStyle?: AuthorTextStyle
    onPress?: () => void
    href?: string
}

const AuthorArea: FunctionComponent<AuthorAreaProps> = ({
    prefixString,
    authors = [],
    testID,
    published,
    dateTime,
    webRel,
}) => {
    const shortedAuthors = authors.slice(0, 2)
    const authorLabels = useMemo(() => {
        const times = Math.max(shortedAuthors.length * 2 - 1, 0)
        const baseTextStyle = {
            fontSize: ['FS14', 'FS14', 'FS16', 'FS18'],
            lineHeight: ['LH16', 'LH16', 'LH20', 'LH24'],
            color: 'onyx',
        }
        return Array(times)
            .fill(times)
            .map((_, index): AuthorTextProps => {
                if (index % 2 === 0 && authors[index / 2]) {
                    const authorIndex = index / 2
                    const author = authors[authorIndex]
                    const onPress = author.onPress
                    return {
                        content: author.name,
                        textStyle: {
                            ...baseTextStyle,
                            textDecorationLine: onPress || author.createHref ? 'underline' : 'none',
                        },
                        onPress: onPress ? (): void => onPress(authorIndex) : undefined,
                        href: author.createHref ? author.createHref(authorIndex) : undefined,
                    }
                }
                return {
                    content: ', ',
                    textStyle: baseTextStyle,
                }
            })
    }, [authors, shortedAuthors.length])

    const authorImageGridProps = useMemo(
        () => ({
            noExpand: true,
            margin: 1,
            backgroundColor: 'pearl',
            borderRadius: IMAGES_SIZES.map(size => size / 2),
            borderColor: 'tahitian-4',
            width: IMAGES_SIZES,
            height: IMAGES_SIZES,
        }),
        []
    )

    return (
        <Grid
            horizontal
            minHeight={COMPONENT_HEIGHTS}
            centerVertical
            noExpand
            backgroundColor="pearl"
            testID={testID}
        >
            {shortedAuthors.map(
                ({ onPress, name, createHref, imageUrl, alt }, index): ReactElement => (
                    <React.Fragment key={name}>
                        <Grid
                            key={`mobile-${name}`}
                            display={['flex', 'flex', 'none']}
                            position="absolute"
                            marginLeft={HALF_MOBILE_IMAGE_SIZE * index}
                            zIndex={shortedAuthors.length - index}
                            border={1}
                            {...authorImageGridProps}
                        >
                            {shortedAuthors.length === 1 ? (
                                <Touchable
                                    href={createHref ? createHref(index) : undefined}
                                    onPress={onPress ? (): void => onPress(index) : undefined}
                                    webRel={webRel}
                                >
                                    <AuthorImage imageUrl={imageUrl} alt={alt} />
                                </Touchable>
                            ) : (
                                <AuthorImage imageUrl={imageUrl} alt={alt} />
                            )}
                        </Grid>
                        <Grid
                            key={`tablet-desktop-${name}`}
                            {...authorImageGridProps}
                            display={['none', 'none', 'flex']}
                            border={[0, 0, 1]} // border still gets rendered with display none on native
                        >
                            <Touchable
                                href={createHref ? createHref(index) : undefined}
                                onPress={onPress ? (): void => onPress(index) : undefined}
                                webRel={webRel}
                            >
                                <AuthorImage imageUrl={imageUrl} alt={alt} />
                            </Touchable>
                        </Grid>
                    </React.Fragment>
                )
            )}
            <Grid
                centerVertical
                marginLeft={
                    authors?.length
                        ? [(shortedAuthors.length + 1) * HALF_MOBILE_IMAGE_SIZE + 10, 3, 3]
                        : 3
                }
                minWidth={0}
            >
                {authorLabels.length ? (
                    <Grid noExpand minWidth={0} marginBottom={1}>
                        <FloatingText
                            numberOfLines={1}
                            textObjects={[
                                {
                                    content: `${prefixString} `,
                                    textStyle: {
                                        fontSize: ['FS14', 'FS14', 'FS16', 'FS18'],
                                        lineHeight: ['LH16', 'LH16', 'LH20', 'LH24'],
                                        fontFamily: 'Sport1-Regular',
                                        color: 'onyx-4',
                                    },
                                },
                                ...authorLabels,
                            ]}
                        />
                    </Grid>
                ) : null}
                <BasicText
                    color="keshi-3"
                    fontSize={['FS14', 'FS14', 'FS16', 'FS18']}
                    lineHeight={['LH16', 'LH16', 'LH20', 'LH24']}
                    numberOfLines={1}
                >
                    {dateTime ? <time dateTime={dateTime}>{published}</time> : published}
                </BasicText>
            </Grid>
        </Grid>
    )
}

export default memo(AuthorArea)
